import { IDebt } from '@axo/form/feature/debt-registry/assently';
import { ReactNode } from 'react';
import { getStoredRefinanceableDebts } from '../../../../../../../components/ExtendedLoanOverview/context/ExtendedLoanOverviewContext/store/getStoredValues';

type Debt = {
  value: number;
  key: string;
  isChecked?: boolean;
  label: string;
  step: number;
  interestRate?: number;
  type?: 'unsecured' | 'cc';
  debtDescription?: string;
  ccDescription?: ReactNode;
  isRefinanceable?: boolean;
};

type GetAllDebtsParams = {
  debts: IDebt[];
  labels?: {
    interestBearing?: string;
    nonInterestBearing?: string;
  };
  loanApplicationID: string;
};

const sortByLoanValue = (a: Debt, b: Debt) => (b?.value ?? 0) - (a?.value ?? 0);

export const getAllDebts = ({
  debts,
  labels,
  loanApplicationID,
}: GetAllDebtsParams): {
  debts: Debt[];
  allocationPriority: string[];
} => {
  const storedDebts = getStoredRefinanceableDebts({
    loanApplicationID,
  });

  if (storedDebts?.length)
    return {
      debts: storedDebts,
      allocationPriority: [...storedDebts.map((debt) => debt.key), 'cash'],
    };

  if (!debts?.length) return { debts: [], allocationPriority: ['cash'] };

  const allDebts = debts?.reduce((acc: Debt[], debt) => {
    if (!debt.nominalInterestRate || !debt.financialInstitutionName) return acc;

    if (debt.type === 'creditFacility') {
      const commonCCDebtData = {
        key: debt.id as string,
        step: 250,
        isChecked: debt.refinanceable,
        interestRate: debt.nominalInterestRate,
        type: 'cc' as const,
        label: debt.financialInstitutionName,
        isRefinanceable: debt.refinanceable,
      };

      if (debt.nonInterestBearingBalance) {
        acc.push({
          ...commonCCDebtData,
          value: debt.nonInterestBearingBalance,
          key: (debt.id + '-nIB') as string,
          ccDescription: labels?.nonInterestBearing ?? 'Interest bearing',
        });
      }

      if (debt.interestBearingBalance) {
        acc.push({
          ...commonCCDebtData,
          value: debt.interestBearingBalance,
          key: (debt.id + '-IB') as string,
        });
      }

      return acc;
    }

    if (!debt.balance) return acc;

    acc.push({
      value: debt.balance,
      label: debt.financialInstitutionName,
      key: debt.id as string,
      step: 250,
      isChecked: debt.refinanceable,
      interestRate: debt.nominalInterestRate,
      type: 'unsecured',
      isRefinanceable: debt.refinanceable,
    });

    return acc;
  }, []);

  const refinanceableDebts = allDebts.filter((debt) => debt.isRefinanceable);
  const nonRefinanceableDebts = allDebts.filter(
    (debt) => !debt.isRefinanceable
  );

  const refinanceableDebtsSorted = refinanceableDebts.sort(sortByLoanValue);
  const nonRefinanceableDebtsSorted =
    nonRefinanceableDebts.sort(sortByLoanValue);

  const debtsFilteredAndSorted = [
    ...refinanceableDebtsSorted,
    ...nonRefinanceableDebtsSorted,
  ];

  const allocationPriority = [
    ...debtsFilteredAndSorted.map((debt) => debt.key),
    'cash',
  ];

  return {
    debts: debtsFilteredAndSorted,
    allocationPriority,
  };
};
